import { AfterViewInit, ChangeDetectionStrategy, Component, ComponentRef, OnInit, QueryList, Type, ViewChild, ViewChildren, ViewContainerRef, computed, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { View } from 'src/app/components/base.component';
import Constants from 'src/app/constants/consts';
import { AuthService } from 'src/app/services/auth.service';
import { DynamicComponentService } from 'src/app/services/dynamic-component.service';
import { UIStateService } from 'src/app/services/ui-state.service';

@Component({
    selector: 'app-generic-modal',
    templateUrl: './generic-modal.component.html',
    styleUrls: ['./generic-modal.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenericModalComponent extends View implements OnInit, AfterViewInit {

    internalRoute = signal<string>(null);
    breadcrumb = signal<string>(null);
    modalTitle = signal<string>(null);
    navigationOverride = signal<boolean>(false);

    public activeModal: NgbActiveModal;
    injectedClass = signal<string>('');
    private injectedComponentType = signal<Type<any> | null>(null);
    private componentDataInitializerFunc = signal<((c: any /** SHOULD BE A COMPONENT FROM COMPONENTREF.INSTANCE */, openedModal: NgbActiveModal, parentModal: GenericModalComponent) => void) | null>(null);
    private readyToInjectComponent = computed(() => !!this.injectedComponentType() && !!this.componentDataInitializerFunc());
    private originalNavigation: string;


    @ViewChild('dynamic', { read: ViewContainerRef }) injectedComponentViewRef: ViewContainerRef
    @ViewChildren(RouterOutlet) routerOutlet: QueryList<RouterOutlet>

    constructor(private router: Router,
        private dynamicService: DynamicComponentService,
        authService: AuthService,
        uiStateService: UIStateService) {
        super(authService, uiStateService);
    }

    ngOnInit(): void {
    }

    // Whether the routed-outlet's own initializer has already run - router.navigate() (called by
    // navigateToRouteForModal, below) resolves asynchronously (route/module resolution, guards),
    // so by the time this component's OWN ngAfterViewInit fires the outlet is still empty far
    // more often than not; that check below is a same-tick fallback for the rare case activation
    // already happened, not the primary path. onRouteActivate (bound to router-outlet's own
    // (activate) output, which fires exactly when a component is actually instantiated into the
    // outlet regardless of how long resolution took) is what reliably calls the initializer -
    // this flag stops both paths from calling it twice for the same modal instance.
    private routeInitializerRan = false;

    ngAfterViewInit(): void {
        if (this.routerOutlet.length && this.routerOutlet.first.component) {
            this.runRouteInitializer(this.routerOutlet.first.component);
        }

        if (this.readyToInjectComponent()) {
            this.dynamicService.injectView(this.injectedComponentViewRef, this.injectedComponentType(), (c: ComponentRef<any>) => this.componentDataInitializerFunc()(c.instance, this.activeModal, this));
        }
        this.subscribe(this.uiStateService.activeTaskEventEmitter, (val => { if (val) { this.closeModal() } }));
    }

    public onRouteActivate(component: any): void {
        this.runRouteInitializer(component);
    }

    private runRouteInitializer(component: any): void {
        if (this.routeInitializerRan || !this.componentDataInitializerFunc()) return;
        this.routeInitializerRan = true;
        this.componentDataInitializerFunc()(component, this.activeModal, this);
    }

    // queryParams: threaded straight into the navigation's own NavigationExtras rather than
    // reaching the routed component through componentDataInitializerFunc, because a
    // loadChildren-registered route (e.g. games-modal, see Constants.OTHER_ROUTED_MODULES) has no
    // component of its own on interactormodaloutlet - Angular activates its own pass-through
    // _ɵEmptyOutletComponent there, and the real leaf component (e.g. GameComponent) only
    // activates one level deeper, into that wrapper's own nested outlet, unreachable from here.
    // A query param survives that nesting and is read directly by the leaf component's own
    // ActivatedRoute - see GameComponent's own questId field for the one current consumer.
    public navigateToRouteForModal(path: string, breadcrumb: string, modalTitle: string, initializer: (c: ComponentRef<any>, openedModal: NgbActiveModal, parentModal: GenericModalComponent) => void, queryParams: Record<string, any> = null) {
        var pathParts = path.split('/');
        var pathArgs = pathParts.length > 1 ? pathParts.pop() : '';
        var modulePath = Constants.OTHER_ROUTED_MODULES.includes(pathParts[0]) ? `${pathParts[0]}-modal` : undefined;


        let navArgs = [];

        let componentPart = pathParts[0];

        if (pathParts.length > 1) {
            navArgs = [...pathParts];
        }
        navArgs.push(`${componentPart}-interactor`);
        if (pathArgs?.length) {
            navArgs.push(pathArgs);
        }

        this.internalRoute.set(path);
        this.breadcrumb.set(breadcrumb);
        this.modalTitle.set(modalTitle);
        this.originalNavigation = this.router.routerState.snapshot.url;

        if (modulePath) navArgs.unshift(modulePath);

        this.router.navigate([{ outlets: { interactormodaloutlet: navArgs } }], { skipLocationChange: true, preserveFragment: false, queryParams: queryParams ?? undefined });
        this.componentDataInitializerFunc.set(initializer);
    }

    public openNestedComponentInModal(type: Type<any>, initializer: (c: ComponentRef<any>, openedModal: NgbActiveModal, parentModal: GenericModalComponent) => void) {
        this.injectedComponentType.set(type);
        this.componentDataInitializerFunc.set(initializer);
    }

    closeModal() {
        this.uiStateService.activeTaskEventEmitter.next(null);
        if (this.originalNavigation) {
            this.router.navigate([{ outlets: { interactormodaloutlet: null } }], { skipLocationChange: true, preserveFragment: false });
        }
        this.activeModal.close();

    }

    cancelModal() {
        this.uiStateService.activeTaskEventEmitter.next(null);
        if (this.originalNavigation) {
            this.router.navigate([{ outlets: { interactormodaloutlet: null } }], { skipLocationChange: true, preserveFragment: false });
        }
        this.activeModal.dismiss();

    }

    setModalData(modal: NgbModalRef, breadcrumb: string, modalTitle: string) {
        this.activeModal = modal;
        this.breadcrumb.set(breadcrumb);
        this.modalTitle.set(modalTitle);
    }

    public setInjectedClass(cls: string): void {
        this.injectedClass.set(cls);
    }

    public setNavigationOverride(value: boolean): void {
        this.navigationOverride.set(value);
    }
}
